import type { Actionplan, ActionplanBatch } from "actionplan/types";
import type { ActionplanStatus } from "api/generated";
import { isActionplanExpired } from "actionplan/utils/actionplanDeadline";

export type ActionplanRowStatus =
  | "requested"
  | "active"
  | "deadlinePassed"
  | "awaitingReview"
  | "underReview"
  | "passed"
  | "failed";

/** Who is reading the row — the two overviews word two of the pills differently. */
export type ActionplanAudience = "teacher" | "student";

/** The CBadge colours an actionplan badge may carry. */
export type ActionplanBadgeColor =
  | "grey"
  | "warning"
  | "orange"
  | "actionplan-active"
  | "info"
  | "positive"
  | "negative";

/** CBadge colours, so the badge reads at a glance. */
export const ACTIONPLAN_ROW_STATUS_COLOR: Record<
  ActionplanRowStatus,
  ActionplanBadgeColor
> = {
  // Nothing has happened to it yet, so it reads quieter than a plan in progress.
  requested: "grey",
  active: "actionplan-active",
  deadlinePassed: "warning",
  awaitingReview: "orange",
  underReview: "orange",
  passed: "positive",
  failed: "negative",
};

/**
 * The pill for the plan's own status, in the reader's words: the two parties
 * call `pending_review` differently, and a request the student has not started
 * carries no pill at all on their own to-do section, where the row has a button
 * instead. `null` means no pill.
 *
 * A teacher does get a pill for a request, and it has to be its own: the batch
 * page lists every plan of one request side by side, so "Verzoek" (untouched)
 * and "Actief" (being written) sit in the same table and must not read alike.
 * The four tabs of the overview used to make that distinction for free.
 *
 * Deliberately blind to the deadline — see `actionplanRowStatus()`.
 */
export const actionplanStatusBadge = (
  actionplan: Pick<Actionplan, "status" | "teacherPassed">,
  audience: ActionplanAudience,
): ActionplanRowStatus | null => {
  if (actionplan.status === "graded") {
    // No verdict, no pill: reading that back as "Niet voldaan" invents a
    // rejection nobody gave.
    if (actionplan.teacherPassed === null) {
      return null;
    }

    return actionplan.teacherPassed === true ? "passed" : "failed";
  }

  if (actionplan.status === "pending_review") {
    return audience === "teacher" ? "underReview" : "awaitingReview";
  }

  if (actionplan.status === "requested") {
    return audience === "teacher" ? "requested" : null;
  }

  return "active";
};

/**
 * Whether the clock still counts for this plan. It stops the moment the student
 * hands it in: from `pending_review` on, the deadline is history and what the
 * row has to say is that someone else is up.
 *
 * The deadline comes in separately because the two shapes keep it in different
 * fields: a list row formats `deadline` for display and keeps the raw value in
 * `deadlineAt`, while the detail carries the raw value in `deadline`.
 */
export const actionplanShowsDeadline = (
  actionplan: Pick<Actionplan, "status">,
  deadlineAt: string | null,
): boolean => {
  const isStudents =
    actionplan.status === "requested" || actionplan.status === "active";

  return isStudents && isActionplanExpired(deadlineAt);
};

/**
 * The single pill a list row carries. A row has room for one, so a passed
 * deadline takes the place of the status while the plan is still the student's.
 */
export const actionplanRowStatus = (
  actionplan: Pick<Actionplan, "status" | "teacherPassed" | "deadlineAt">,
  audience: ActionplanAudience,
): ActionplanRowStatus | null => {
  // `deadlineAt`, not `deadline`: the latter is already formatted for display
  // ("15 maart 2026"), which `new Date()` cannot parse for every month.
  if (actionplanShowsDeadline(actionplan, actionplan.deadlineAt)) {
    return "deadlinePassed";
  }

  return actionplanStatusBadge(actionplan, audience);
};

/** CBadge colours for the plain status pill a grouped row carries. */
export const ACTIONPLAN_STATUS_COLOR: Record<
  ActionplanStatus,
  ActionplanBadgeColor
> = {
  requested: "grey",
  active: "actionplan-active",
  pending_review: "orange",
  graded: "positive",
};

/**
 * Which of the two vocabularies a badge speaks: a plan's own row status, or the
 * plain status of a request that covers several plans.
 */
export type ActionplanBatchBadge =
  | { kind: "rowStatus"; value: ActionplanRowStatus }
  | { kind: "status"; value: ActionplanStatus };

/**
 * The pill a grouped teacher row carries. A batch of one is an ordinary plan, so
 * it keeps the plan's own pill — "Voldaan" and all. A bigger batch has no single
 * verdict to report (its progress is the row's "2/25"), so it shows the plain
 * status instead, with a passed deadline still taking precedence while the plans
 * are the students' to write.
 */
export const actionplanBatchBadge = (
  batch: Pick<
    ActionplanBatch,
    "total" | "status" | "teacherPassed" | "deadlineAt"
  >,
  audience: ActionplanAudience = "teacher",
): ActionplanBatchBadge | null => {
  if (batch.total === 1) {
    const rowStatus = actionplanRowStatus(batch, audience);

    return rowStatus === null ? null : { kind: "rowStatus", value: rowStatus };
  }

  if (actionplanShowsDeadline(batch, batch.deadlineAt)) {
    return { kind: "rowStatus", value: "deadlinePassed" };
  }

  return { kind: "status", value: batch.status };
};
